import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { eventName, eventPlace, noteGroups, noteTime } from '@wr/shared';
import { Icon } from '../../components/Icon.js';
import { useOpen } from '../../components/Shell.js';
import { NAV } from '../../nav.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { todoItems, type TodoItem } from '../leads/data.js';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface ShowNumbers {
  scansToday: number;
  contacts: number;
  leads: number;
  toDocument: number;
}

/**
 * The numbers of the event card, from what this device keeps (it works
 * without network): the event's contacts and leads, the person's scans of
 * today (a scan not sent yet counts), and their own records to document.
 */
async function showNumbers(
  store: LocalStore,
  userId: string,
  eventId: string,
  todo: TodoItem[],
): Promise<ShowNumbers> {
  const [contacts, leads] = await Promise.all([
    store.listRecords('contact'),
    store.listRecords('lead'),
  ]);
  const atShow = (r: LocalRecord) => !r.deleted && r.event_id === eventId;
  const eventContacts = contacts.filter(atShow);
  const eventLeads = leads.filter(atShow);
  const ids = new Set([...eventContacts, ...eventLeads].map((r) => r.id));
  const mine = (r: LocalRecord) => !r.created_by || r.created_by === userId;
  const isToday = (r: LocalRecord) => {
    if (typeof r.created_at !== 'string') return true;
    const d = new Date(r.created_at);
    return (
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` ===
      today()
    );
  };
  return {
    scansToday: eventContacts.filter((c) => c.source === 'scan' && mine(c) && isToday(c)).length,
    contacts: eventContacts.length,
    leads: eventLeads.length,
    toDocument: todo.filter((t) => ids.has(t.id)).length,
  };
}

/** The person's own records to document (To do), refreshed after each save or sync. */
export function useTodo(): TodoItem[] | null {
  const { user } = useSignedIn();
  const { store, counts } = useOffline();
  const [items, setItems] = useState<TodoItem[] | null>(null);
  useEffect(() => {
    if (!store) return;
    let current = true;
    void todoItems(store, user.id).then((list) => current && setItems(list));
    return () => {
      current = false;
    };
  }, [store, user.id, counts]);
  return items;
}

/**
 * Home at a show (brief section 5, docs/prototype.html): the event card
 * and its numbers, the big Scan button, the quick actions, To document,
 * and the notes shared with the team.
 */
export function HomeAtShow({ event }: { event: LocalRecord }) {
  const { user, can } = useSignedIn();
  const { store, counts } = useOffline();
  const open = useOpen();
  const todo = useTodo();
  const [numbers, setNumbers] = useState<ShowNumbers | null>(null);

  useEffect(() => {
    if (!store || !todo) return;
    let current = true;
    void showNumbers(store, user.id, event.id, todo).then((n) => current && setNumbers(n));
    return () => {
      current = false;
    };
  }, [store, user.id, event.id, todo, counts]);

  const name = eventName(event);
  return (
    <>
      <div className="event-card">
        <div className="ev-row">
          <div>
            <span className="lbl">Current event</span>
            <span className="ev">{name}</span>
            <span className="lbl">{eventPlace(event)}</span>
          </div>
          {NAV.travel.allowed(can) && (
            <span className="actions">
              <button
                type="button"
                className="linkbtn"
                onClick={() => open(`${NAV.travel.path}/${event.id}`)}
              >
                Event info
              </button>
            </span>
          )}
        </div>
        <div className="nums" aria-label={`Numbers of ${name}`}>
          <div>
            <b>{numbers?.scansToday ?? 0}</b>
            <span>Your scans today</span>
          </div>
          <div>
            <b>{numbers?.contacts ?? 0}</b>
            <span>Contacts</span>
          </div>
          <div>
            <b>{numbers?.leads ?? 0}</b>
            <span>Leads</span>
          </div>
          <div>
            <b className={numbers?.toDocument ? 'red' : undefined}>{numbers?.toDocument ?? 0}</b>
            <span>To document</span>
          </div>
        </div>
      </div>
      {NAV.scan.allowed(can) && (
        <button
          type="button"
          className="btn primary huge scan-cta"
          onClick={() => open(NAV.scan.path)}
        >
          <Icon name="scan" />
          Scan a badge or a card
        </button>
      )}
      <div className="quick-row">
        {NAV.addContact.allowed(can) && (
          <button type="button" className="btn sm" onClick={() => open(NAV.addContact.path)}>
            <Icon name="personadd" />
            Add contact
          </button>
        )}
        {NAV.addLead.allowed(can) && (
          <button type="button" className="btn sm" onClick={() => open(NAV.addLead.path)}>
            <Icon name="leads" />
            Add lead
          </button>
        )}
        {can('contacts.handoff') && (
          <button type="button" className="btn sm" onClick={() => open('/visitor')}>
            Hand the tablet to a visitor
          </button>
        )}
      </div>
      <ToDocumentCard items={todo} />
      {can('events.read') && <EventNotes event={event} />}
    </>
  );
}

/**
 * To document (brief section 5): the person's leads to document and
 * contacts to correct, the way to the list, and Complete by voice (the
 * review call, build step 5).
 */
export function ToDocumentCard({
  items,
  office,
}: {
  items: TodoItem[] | null;
  /** At the office, one line (prototype). */
  office?: boolean;
}) {
  const { can } = useSignedIn();
  const open = useOpen();
  const navigate = useNavigate();
  const location = useLocation();
  if (!items || !can('todo.use')) return null;
  const leads = items.filter((i) => i.kind === 'lead').length;
  const contacts = items.filter((i) => i.kind === 'contact').length;
  return (
    <section className="ncard eod-card">
      <div className="ncard-h">
        <h2 className="sec">To document</h2>
        <span className="note">Once documented, it goes to HubSpot by itself</span>
      </div>
      {office ? (
        <p>
          {items.length > 0 ? (
            <>
              <b>{items.length}</b> {items.length === 1 ? 'record' : 'records'} to document before
              HubSpot.
            </>
          ) : (
            'Everything is documented.'
          )}
        </p>
      ) : (
        <ul className="exit eod-steps">
          <li className={leads ? undefined : 'ok'}>
            <span className="xb">{leads || '✓'}</span>
            <span>
              {leads
                ? `${leads === 1 ? 'lead' : 'leads'} to document (Discovery Capture)`
                : 'Every lead is documented'}
            </span>
          </li>
          <li className={contacts ? undefined : 'ok'}>
            <span className="xb">{contacts || '✓'}</span>
            <span>
              {contacts
                ? `${contacts === 1 ? 'contact' : 'contacts'} to correct`
                : 'Every contact is ready'}
            </span>
          </li>
        </ul>
      )}
      <div className="actions">
        {items.length > 0 && can('todo.voice') && (
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate('/todo/voice', { state: { from: location.pathname } })}
          >
            <Icon name="mic" />
            Complete by voice
          </button>
        )}
        <button type="button" className="btn" onClick={() => open(NAV.todo.path)}>
          {!office && <Icon name="edit" />}
          See the list
        </button>
      </div>
    </section>
  );
}

/**
 * The notes of the event (prototype: Notes for the event): shared with
 * everyone on it, with an optional day and time; anyone checks one off,
 * its author (or an admin) deletes it. Saved on the device first.
 */
function EventNotes({ event }: { event: LocalRecord }) {
  const { user, can } = useSignedIn();
  const { store, counts, save } = useOffline();
  const [notes, setNotes] = useState<LocalRecord[]>([]);
  const [text, setText] = useState('');
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  const write = can('events.notes');

  useEffect(() => {
    if (!store) return;
    let current = true;
    void store.listRecords('event_note').then((list) => {
      if (current) setNotes(list.filter((n) => !n.deleted && n.event_id === event.id));
    });
    return () => {
      current = false;
    };
  }, [store, counts, event.id]);

  const me = `${user.firstName} ${user.lastName}`;
  const mine = (n: LocalRecord) => !n.created_by || n.created_by === user.id;
  const author = (n: LocalRecord) =>
    mine(n) ? `${me} (you)` : typeof n.author_name === 'string' ? n.author_name : '';
  const change = (n: LocalRecord, fields: Record<string, unknown>) =>
    save({
      opId: newId(),
      entity: 'event_note',
      id: n.id,
      action: 'upsert',
      fields,
      changedAt: new Date().toISOString(),
    });

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    await save({
      opId: newId(),
      entity: 'event_note',
      id: newId(),
      action: 'upsert',
      fields: { event_id: event.id, text: text.trim(), day: day || null, time: time || null },
      changedAt: new Date().toISOString(),
    });
    setText('');
    setDay('');
    setTime('');
  }

  const groups = noteGroups(notes, today());
  return (
    <section className="ncard">
      <div className="ncard-h">
        <h2 className="sec">Notes for {eventName(event)}</h2>
        <span className="note">Shared with the team at the booth</span>
      </div>
      {write && (
        <form className="nadd" onSubmit={(e) => void add(e)}>
          <input
            type="text"
            id="note-tx"
            placeholder="Visit X, Pieter comes by at 15:00..."
            aria-label="New note"
            maxLength={2000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <label className="nopt">
            <span>Day, optional</span>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
          <label className="nopt">
            <span>Time, optional</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
          <button type="submit" className="btn sm primary">
            Add
          </button>
        </form>
      )}
      {groups.length === 0 && (
        <p className="note">No note yet. Visits, meetings, a dinner: the team sees them here.</p>
      )}
      {groups.map((g) => (
        <Fragment key={g.title}>
          <div className={`nday${g.today ? ' today' : ''}`}>{g.title}</div>
          <ul className="nlist">
            {g.notes.map((n) => {
              const done = n.done === true;
              const time = noteTime(n.time);
              return (
                <li key={n.id} className={done ? 'done' : undefined}>
                  <button
                    type="button"
                    className="chk"
                    disabled={!write}
                    aria-label={`${done ? 'Reopen' : 'Done'}: ${String(n.text)}`}
                    aria-pressed={done}
                    onClick={() => void change(n, { done: !done })}
                  />
                  <span className="ntx">
                    {time && <b>{time}</b>} {String(n.text)}
                    <small>{author(n)}</small>
                  </span>
                  {(mine(n) || can('events.manage')) && write && (
                    <button
                      type="button"
                      className="x sm"
                      aria-label={`Delete note: ${String(n.text)}`}
                      onClick={() =>
                        void save({
                          opId: newId(),
                          entity: 'event_note',
                          id: n.id,
                          action: 'delete',
                          changedAt: new Date().toISOString(),
                        })
                      }
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </Fragment>
      ))}
    </section>
  );
}
